import clsx from 'clsx'
import { FC } from 'react'
import styles from './ShareMenu.module.css'

const FacebookIcon: FC = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M13.4 21v-8.2h2.75l.41-3.2H13.4V7.56c0-.92.26-1.55 1.58-1.55h1.69V3.14c-.29-.04-1.3-.13-2.46-.13-2.44 0-4.1 1.49-4.1 4.22v2.36H7.35v3.2h2.75V21h3.3z" />
  </svg>
)

const XIcon: FC = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18.9 2.1h3.05l-6.66 7.61L23.12 22h-6.14l-4.8-6.28L6.68 22H3.62l7.12-8.14L3.23 2.1h6.3l4.34 5.74 5.03-5.74Zm-1.07 18.08h1.69L8.62 3.83H6.81l11.02 16.35Z" />
  </svg>
)

const EmailIcon: FC = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
)

const CopyIcon: FC = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
)

interface ShareMenuProps {
  /** Absolute URL being shared. */
  url: string
  /** Subject for the email item; typically document.title. */
  title: string
  /** Which edge of the trigger the menu hangs from. */
  align: 'left' | 'right'
  ariaLabel: string
  /** Copy URL was chosen — the caller owns the clipboard write and its toast. */
  onCopy: () => void
  /** Any item was chosen — the caller closes the menu. */
  onSelect: () => void
  /** `'soft'`: the conversation panel's look — a 12px card, hairline border, soft shadow, no
   *  caret, compact 14px rows with a grey hover fill. Default: the article share bar's menu. */
  variant?: 'default' | 'soft'
}

// The one share menu for the site: comments and articles render the same
// four targets so a reader sees one vocabulary. Positioned absolutely under
// the trigger; the caller provides the `position: relative` wrapper.
const ShareMenu: FC<ShareMenuProps> = ({
  url,
  title,
  align,
  ariaLabel,
  onCopy,
  onSelect,
  variant = 'default',
}) => {
  const encoded = encodeURIComponent(url)
  return (
    <div
      className={clsx(styles.menu, align === 'right' ? styles.alignRight : styles.alignLeft, {
        [styles.soft]: variant === 'soft',
      })}
      role="menu"
      aria-label={ariaLabel}
    >
      <a
        role="menuitem"
        className={styles.item}
        target="_blank"
        rel="noopener noreferrer"
        href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`}
        onClick={onSelect}
      >
        <FacebookIcon />
        {'Facebook'}
      </a>
      <a
        role="menuitem"
        className={styles.item}
        target="_blank"
        rel="noopener noreferrer"
        href={`https://x.com/intent/post?url=${encoded}`}
        onClick={onSelect}
      >
        <XIcon />
        {'X'}
      </a>
      <a
        role="menuitem"
        className={styles.item}
        href={`mailto:?subject=${encodeURIComponent(title)}&body=${encoded}`}
        onClick={onSelect}
      >
        <EmailIcon />
        {'Email'}
      </a>
      <button
        type="button"
        role="menuitem"
        className={styles.item}
        onClick={() => {
          onSelect()
          onCopy()
        }}
      >
        <CopyIcon />
        {'Copy URL'}
      </button>
    </div>
  )
}

export default ShareMenu
